import { Component, Input, OnChanges, OnInit, OnDestroy, SimpleChanges } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { NgxCurrencyDirective } from 'ngx-currency';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { LookupItemStruct } from '@shared/services/structs/pessoa/lookup-item.struct';
import { UnidadeMedidaStruct } from '@shared/services/structs/produto/unidade-medida.struct';
import { TagListItem } from '@shared/services/structs/produto/tag-list-item.struct';
import { Masks } from '@shared/services/mask.service';
import { NgxMaskDirective } from 'ngx-mask';
import { ProdutoFornecedor } from '@shared/services/models/produto/produto-fornecedor.model';
import { AnpLookupService } from '@shared/services/apis/motor-fiscal/anp-lookup.service';
import { AnpNcmAtivoStruct } from '@shared/services/structs/motor-fiscal/anp-ncm-ativo.struct';
import { AnpCodigoLookupStruct } from '@shared/services/structs/motor-fiscal/anp-codigo-lookup.struct';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { AbntPipe } from '@shared/pipes/abnt.pipe';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';

/** Sentinela do código ANVISA para produto isento (valor que vai no cProdANVISA da NF-e). */
const ANVISA_ISENTO = 'ISENTO';

@Component({
  selector: 'app-produto-outros',
  standalone: true,
  imports: [
    DecimalPipe,
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    NgxMaskDirective,
    SelectPesquisavelComponent,
    EmptyStateComponent,
    AbntPipe,
    AlertCardComponent,
  ],
  templateUrl: './produto-outros.component.html',
  styleUrl: './produto-outros.component.css',
})
export class ProdutoOutrosComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) form!: FormGroup;
  @Input() listaLinhaProduto: LookupItemStruct[] = [];
  @Input() listaTag: TagListItem[] = [];
  @Input() listaUnidadeMedida: UnidadeMedidaStruct[] = [];
  @Input() masks!: Masks;
  @Input() percentageMask!: any;
  @Input() defaultCurrencyMask!: any;
  @Input() dynamicCurrencyMask!: any;
  @Input() quantidadeMask!: any;
  @Input() urlFornecedor: string = '';
  /** Kit não possui múltiplo próprio — ele apenas consome o dos componentes (RN04). */
  @Input() isKit: boolean = false;
  @Input() set dadosFornecedoresCarregados(dados: ProdutoFornecedor[]) {
    if (dados) {
      this.fornecedores.clear();
      dados.forEach(item => this.addFornecedorRow(item));
    }
  }

  // ── Dados ANP / ANVISA ──────────────────────────────────────────────────────
  // Listas baixadas uma vez; o match de NCM é local (regra/nível vêm prontos do backend).
  public ncmsAtivos: AnpNcmAtivoStruct[] = [];
  public codigosAnp: AnpCodigoLookupStruct[] = [];
  public anpMatch: AnpNcmAtivoStruct | null = null;
  public showMedicamento = false;
  public isIsentoAnvisa = false;
  private ncmSub?: Subscription;
  private unidadeMedidaSub?: Subscription;

  constructor(private fb: FormBuilder, private anpLookupService: AnpLookupService) {}

  ngOnInit(): void {
    this.anpLookupService.getAnpParaProduto().subscribe({
      next: r => {
        if (r.isError) return;
        this.ncmsAtivos = r.ncms ?? [];
        this.codigosAnp = r.codigos ?? [];
        this.recalcularAnp(this.form.get('ncm')?.value);
      },
    });

    this.sincronizarAnvisa();

    this.ncmSub = this.form.get('ncm')?.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe(ncm => this.recalcularAnp(ncm));

    this.unidadeMedidaSub = this.form.get('idUnidadeMedida')?.valueChanges
      .pipe(distinctUntilChanged())
      .subscribe(id => {
        this.limparMultiploSeIgualAUnidadeDoProduto(id);
        this.atualizarListaUnidadeMultiplo();
      });

    this.atualizarListaUnidadeMultiplo();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['listaUnidadeMedida']) this.atualizarListaUnidadeMultiplo();
  }

  ngOnDestroy(): void {
    this.ncmSub?.unsubscribe();
    this.unidadeMedidaSub?.unsubscribe();
  }

  // ── ANP ──

  /**
   * Filtro de EXIBIÇÃO: decide quais subseções mostrar a partir do NCM digitado.
   * O nível e a lista de NCMs sujeitos vêm prontos do backend — aqui só comparamos strings.
   */
  private recalcularAnp(ncm: string | null | undefined): void {
    const n = (ncm ?? '').replace(/\D/g, '');
    this.anpMatch = this.ncmsAtivos
      .filter(x => n.startsWith(x.codigoNcm))
      .sort((a, b) => b.codigoNcm.length - a.codigoNcm.length)[0] ?? null;

    this.showMedicamento = n.startsWith('3004');

    if (!this.anpMatch) this.form.get('idAnpEmpresa')?.setValue(null);

    // Cobre o modo edição: ao popular o form (patchValue dispara o NCM),
    // sincroniza o estado de isenção a partir do codigoAnvisa carregado.
    this.sincronizarAnvisa();
  }

  get idAnpEmpresaControl() {
    return this.form.get('idAnpEmpresa') as FormControl;
  }

  /** Código ANP selecionado no dropdown (para o painel read-only). */
  get anpSelecionado(): AnpCodigoLookupStruct | undefined {
    const id = this.idAnpEmpresaControl?.value;
    return this.codigosAnp.find(c => c.idAnpEmpresa === id);
  }

  /** Ao selecionar um código ANP, fixa a Unidade de Medida Tributável do produto. */
  onAnpSelecionado(idAnpEmpresa: number): void {
    const item = this.codigosAnp.find(c => c.idAnpEmpresa === idAnpEmpresa);
    if (item) this.idUnidadeTributavelControl?.setValue(item.idUnidadeMedida);
  }

  /** Abreviação da unidade (resolvida pela lista de unidades já carregada). */
  unidadeAbreviacao(idUnidadeMedida: number): string {
    const u = this.listaUnidadeMedida.find(x => x.idUnidadeMedida === idUnidadeMedida);
    return u?.abreviacao || '—';
  }

  // ── ANVISA ──

  get codigoAnvisaControl() {
    return this.form.get('codigoAnvisa') as FormControl;
  }
  get motivoIsencaoAnvisaControl() {
    return this.form.get('motivoIsencaoAnvisa') as FormControl;
  }

  /**
   * Deriva o estado de isenção do valor atual de codigoAnvisa (sem apagar valores).
   * Usado no init e ao carregar um produto em edição.
   */
  private sincronizarAnvisa(): void {
    const codigo = this.codigoAnvisaControl;
    const motivo = this.motivoIsencaoAnvisaControl;
    if (!codigo) return;

    this.isIsentoAnvisa = (codigo.value ?? '') === ANVISA_ISENTO;

    if (this.isIsentoAnvisa) {
      if (!codigo.disabled) codigo.disable({ emitEvent: false });
      motivo?.setValidators([Validators.required, Validators.maxLength(255)]);
    } else {
      if (codigo.disabled) codigo.enable({ emitEvent: false });
      motivo?.clearValidators();
    }
    motivo?.updateValueAndValidity({ emitEvent: false });
  }

  /** Liga/desliga isenção: trava o código (= "ISENTO") e torna o motivo obrigatório. */
  onToggleIsento(isento: boolean): void {
    this.isIsentoAnvisa = isento;
    const codigo = this.codigoAnvisaControl;
    const motivo = this.motivoIsencaoAnvisaControl;

    if (isento) {
      codigo?.setValue(ANVISA_ISENTO);
      codigo?.disable();
      motivo?.setValidators([Validators.required, Validators.maxLength(255)]);
    } else {
      codigo?.enable();
      codigo?.setValue('');
      motivo?.clearValidators();
      motivo?.setValue('');
    }
    codigo?.updateValueAndValidity();
    motivo?.updateValueAndValidity();
  }

  get idLinhaProdutoControl() {
    return this.form.get('idLinhaProduto') as FormControl;
  }
  get tagsControl() {
    return this.form.get('tags') as FormControl;
  }
  get idUnidadeTributavelControl() {
    return this.form.get('idUnidadeTributavel') as FormControl;
  }

  // ── Controle de múltiplo ────────────────────────────────────────────────────

  get idUnidadeMultiploControl() {
    return this.form.get('idUnidadeMultiplo') as FormControl;
  }

  get isControlaMultiplo(): boolean {
    return this.form.get('isControlaMultiplo')?.value === true;
  }

  /**
   * Opções da unidade de múltiplo: tudo que a empresa tem, MENOS a unidade de medida do próprio
   * produto — não existe múltiplo de si mesmo.
   *
   * CAMPO, e não getter: vai em `[offlineList]`, e o `select-pesquisavel` reprocessa a cada
   * referência nova — um `.filter()` por ciclo de detecção o fazia trabalhar sem parar.
   * Recalculado quando a lista chega e quando a unidade do produto muda.
   */
  listaUnidadeMultiplo: UnidadeMedidaStruct[] = [];

  private atualizarListaUnidadeMultiplo(): void {
    const idUnidadeMedida = this.form?.get('idUnidadeMedida')?.value;
    this.listaUnidadeMultiplo = !idUnidadeMedida
      ? this.listaUnidadeMedida
      : this.listaUnidadeMedida.filter(u => u.idUnidadeMedida !== idUnidadeMedida);
  }

  /**
   * Sigla da unidade de múltiplo escolhida, ou null enquanto não houver escolha.
   * A formatação do fator fica no template, via pipe `abnt:'quantidade'`.
   */
  get abreviacaoUnidadeMultiplo(): string | null {
    const unidade = this.listaUnidadeMedida.find(
      u => u.idUnidadeMedida === this.form.get('idUnidadeMultiplo')?.value
    );
    return unidade?.abreviacao ?? null;
  }

  /**
   * Alerta de atenção quando só um dos dois campos está preenchido (CA04/CA05).
   * Com os dois vazios não há alerta — os mat-error dos campos já cobrem esse caso.
   */
  get alertaMultiploTexto(): string | null {
    if (!this.isControlaMultiplo) return null;

    const unidade = this.abreviacaoUnidadeMultiplo;
    const fator = Number(this.form.get('fatorMultiplo')?.value ?? 0);

    if (unidade && !(fator > 0)) {
      return `Informe o fator do múltiplo (maior que zero) para a unidade ${unidade}.`;
    }
    if (!unidade && fator > 0) {
      return 'Informe a unidade de múltiplo correspondente ao fator informado.';
    }

    // `embalagens × fator` sai na unidade do produto: se ela não fraciona, um fator com casas nunca
    // fecha inteiro e a venda fica impossível. A API recusa com a mesma regra.
    const unidadeProduto = this.listaUnidadeMedida.find(
      u => u.idUnidadeMedida === this.form.get('idUnidadeMedida')?.value
    );
    if (unidadeProduto?.isFracionada === false && fator > 0 && fator % 1 !== 0) {
      return `A unidade ${unidadeProduto.abreviacao} não aceita fração: o fator do múltiplo deve ser um número inteiro.`;
    }
    return null;
  }

  /** Só mostra a conversão (RN07) quando o par está completo e o fator é maior que zero. */
  get mostrarConversaoMultiplo(): boolean {
    const fator = this.form.get('fatorMultiplo')?.value;
    return this.isControlaMultiplo && !!this.abreviacaoUnidadeMultiplo && Number(fator) > 0;
  }

  /**
   * A unidade de medida do produto é editável em Dados Gerais. Se ela passar a ser a mesma que já
   * está escolhida como múltiplo, a escolha deixa de ser válida e é limpa — aí o
   * controleMultiploValidator volta a acusar o campo como obrigatório.
   */
  private limparMultiploSeIgualAUnidadeDoProduto(idUnidadeMedida: number | null): void {
    if (idUnidadeMedida && this.idUnidadeMultiploControl?.value === idUnidadeMedida) {
      this.idUnidadeMultiploControl.setValue(null);
    }
  }
  get fornecedores(): FormArray {
    return this.form.get('fornecedores') as FormArray;
  }

  getControl(item: any, controlName: string): FormControl {
    return item.get(controlName) as FormControl;
  }

  setFornecedorPrincipal(index: number, event: any): void {
    this.fornecedores.controls.forEach((control, i) => {
      control.get('isPrincipal')?.setValue(i === index && event.checked);
    });
  }

  addFornecedorRow(data?: any): void {
    const group = this.fb.group({
      idFornecedor: [data?.idFornecedor, Validators.required],
      codigoNoFornecedor: [data?.codigoNoFornecedor || ''],
      isPrincipal: [data?.isPrincipal || false],
    });
    this.fornecedores.push(group);
  }

  removeFornecedorRow(index: number): void {
    this.fornecedores.removeAt(index);
  }
}
